import React, { useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { hasAnyRole, ROLE_LABELS, type Role } from '../auth/roles'
import pageStyles from '../styles/page.module.scss'

// The groups come from the ID token, which can predate a role granted on the
// Users page by up to 15 minutes. A refusal here happens before any API call,
// so the API's 403 retry never runs: refresh the token once instead, and the
// gate re-renders with the current roles.
const useRefreshOnce = () => {
  const { refreshRoles } = useAuth()
  const refreshed = useRef(false)
  useEffect(() => {
    if (refreshed.current) return
    refreshed.current = true
    refreshRoles().catch(() => {})
  }, [refreshRoles])
}

interface RequireRoleProps {
  roles: readonly Role[]
  children: React.ReactNode
}

// A 403 panel rather than a redirect: links are shared between people with
// different roles, and a silent redirect looks like a broken link. The API
// enforces the same roles; this gate is for usability only.
export const RequireRole: React.FC<RequireRoleProps> = ({ roles, children }) => {
  const { roles: held } = useAuth()
  if (hasAnyRole(held, roles)) return <>{children}</>
  return <AccessDenied roles={roles} />
}

const AccessDenied: React.FC<{ roles: readonly Role[] }> = ({ roles }) => {
  useRefreshOnce()
  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.error}>
        <h2>You do not have access to this page</h2>
        <p>
          This page needs the {roles.map((role) => ROLE_LABELS[role]).join(' or ')} role. Ask a CMS
          admin to add it.
        </p>
      </div>
    </div>
  )
}

// Shown in place of every page to a signed-in person with no role. A new
// invite starts here until an admin assigns a role.
export const NoRoleAssigned: React.FC = () => {
  useRefreshOnce()
  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.error}>
        <h2>No access assigned</h2>
        <p>Your account has no CMS role yet. Ask a CMS admin to give you one, then reload this page.</p>
      </div>
    </div>
  )
}

export default RequireRole
